import type { ProblemReportCategory } from "@/features/feedback/types";

import posthog from "@/lib/posthog";

const MIN_DESCRIPTION_LENGTH = 15;

export interface CaptureProblemReportedInput {
	category: ProblemReportCategory;
	description: string;
	pagePath: string;
	pageUrl: string;
}

type PostHogSessionTools = typeof posthog & {
	get_session_id?: () => string | undefined;
	get_session_replay_url?: (options?: {
		timestampLookBack?: number;
		withTimestamp?: boolean;
	}) => string | undefined;
};

/**
 * Capture a dashboard problem report with session replay context when available.
 */
export function captureProblemReported({
	category,
	description,
	pagePath,
	pageUrl,
}: CaptureProblemReportedInput) {
	const trimmedDescription = description.trim();
	if (!isProblemReportDescriptionValid(trimmedDescription)) {
		return false;
	}

	const posthogWithSession = posthog as PostHogSessionTools;
	const sessionReplayUrl = posthogWithSession.get_session_replay_url?.({
		timestampLookBack: 60,
		withTimestamp: true,
	});
	const sessionId = posthogWithSession.get_session_id?.();

	posthog.capture("problem_reported", {
		category,
		description: trimmedDescription,
		page_path: pagePath,
		page_url: pageUrl,
		...(sessionId && { session_id: sessionId }),
		...(sessionReplayUrl && { session_replay_url: sessionReplayUrl }),
	});

	return true;
}

export function isProblemReportDescriptionValid(description: string) {
	return description.trim().length >= MIN_DESCRIPTION_LENGTH;
}
